import Navbar from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Award, Users, GraduationCap, Target, Heart } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-ags-bg">
      <Navbar />
      
      {/* Hero */}
      <section className="py-16 md:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl text-ags-text tracking-tight leading-tight mb-6">
                About AGS-HK
              </h1>
              <p className="text-lg text-ags-text-secondary leading-relaxed">
                The Association of Ghanaian Scholars in Hong Kong (AGS-HK) is a community organization 
                dedicated to uniting Ghanaian students, researchers, and academics across Hong Kong's 
                universities and institutions.
              </p>
            </div>
            <div className="relative">
              <img 
                src="/images/about-members.jpg"
                alt="AGS-HK Members" 
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-slate-200">
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-sm bg-ags-blue flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <h2 className="font-heading text-2xl text-ags-text mb-4">Our Mission</h2>
                <p className="text-ags-text-secondary leading-relaxed">
                  To foster a supportive community that promotes academic excellence, professional 
                  development, and cultural exchange among Ghanaian scholars in Hong Kong.
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-sm bg-ags-gold flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <h2 className="font-heading text-2xl text-ags-text mb-4">Our Vision</h2>
                <p className="text-ags-text-secondary leading-relaxed">
                  To be the premier network connecting Ghanaian scholars in Hong Kong, creating 
                  lasting impact through collaboration, mentorship, and community building.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl text-ags-text tracking-tight mb-4">
              Our Values
            </h2>
            <p className="text-ags-text-secondary max-w-2xl mx-auto">
              The principles that guide everything we do at AGS-HK.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-ags-blue/10 flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-ags-blue" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl text-ags-text mb-2">Excellence</h3>
              <p className="text-sm text-ags-text-secondary">
                We strive for academic and professional excellence in all our endeavors.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-ags-blue/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-ags-blue" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl text-ags-text mb-2">Community</h3>
              <p className="text-sm text-ags-text-secondary">
                We believe in the power of community to support and uplift one another.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-ags-blue/10 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-ags-blue" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl text-ags-text mb-2">Service</h3>
              <p className="text-sm text-ags-text-secondary">
                We are committed to giving back and serving both our community and Ghana.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl text-ags-text tracking-tight mb-8 text-center">
            Our History
          </h2>
          <div className="prose prose-slate max-w-none">
            <p className="text-ags-text-secondary leading-relaxed mb-6">
              AGS-HK was established in 2015 by a group of Ghanaian scholars who recognized the need 
              for a unified platform to connect Ghanaians studying and working in Hong Kong's academic 
              institutions.
            </p>
            <p className="text-ags-text-secondary leading-relaxed mb-6">
              Since then, we have grown to include members from all major universities in Hong Kong, 
              spanning undergraduate students to senior researchers and alumni. Our community continues 
              to thrive through regular events, mentorship programs, and collaborative initiatives.
            </p>
            <p className="text-ags-text-secondary leading-relaxed">
              Today, AGS-HK serves as a vital network for Ghanaian scholars, facilitating connections, 
              sharing opportunities, and celebrating our shared heritage while contributing to the 
              academic and cultural landscape of Hong Kong.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center text-sm text-ags-text-muted">
          <p>© {new Date().getFullYear()} Association of Ghanaian Scholars in Hong Kong. All rights reserved.</p>
          <p className="mt-2">Built by Asamoah Joseph</p>
        </div>
      </footer>
    </div>
  );
};

export default About;
